import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Papa from 'papaparse';
import { usePageTitle } from '../hooks/usePageTitle';
import { buildWrappedStatsForUser } from '../lib/wrapped/buildWrappedStats';
import type { LedgerRow, LogRow } from '../lib/wrapped/wrappedTypes';
import { WrappedScreenWrapper, type WrappedNavigationRef } from '../components/wrapped/WrappedScreenWrapper';
import { TitleCard } from '../components/wrapped/cards/TitleCard';
import { InitiationCard } from '../components/wrapped/cards/InitiationCard';
import { TotalMinedCard } from '../components/wrapped/cards/TotalMinedCard';
import { HighestSessionCard } from '../components/wrapped/cards/HighestSessionCard';
import { WingoPerSessionCard } from '../components/wrapped/cards/WingoPerSessionCard';
import { WagersCard } from '../components/wrapped/cards/WagersCard';
import { SpecialUnlocksCard } from '../components/wrapped/cards/SpecialUnlocksCard';
import { OldBalanceCard } from '../components/wrapped/cards/OldBalanceCard';
import { DaisyTrustCard } from '../components/wrapped/cards/DaisyTrustCard';
import { NextYearCard } from '../components/wrapped/cards/NextYearCard';
import { ShareCard } from '../components/wrapped/cards/ShareCard';
import { DaisyCategoryCard } from '../components/wrapped/cards/DaisyCategoryCard';
import { WingateSatelliteCard } from '../components/wrapped/cards/WingateSatelliteCard';
import { SaveTheDateCard } from '../components/wrapped/cards/SaveTheDateCard';
import { OBWIPrizeCard } from '../components/wrapped/cards/OBWIPrizeCard';
import { WingipediaCard } from '../components/wrapped/cards/WingipediaCard';
import { WingonomicsCard } from '../components/wrapped/cards/WingonomicsCard';
import { MinedWithDaisyCard } from '../components/wrapped/cards/MinedWithDaisyCard';
import { OBWIVideoCard } from '../components/wrapped/cards/OBWIVideoCard';
import { getAvatarForUser } from '../lib/wrapped/avatarUtils';
import { WrappedProvider } from '../components/wrapped/WrappedContext';

/**
 * Convert username to URL-friendly format
 * - Lowercase
 * - Replace spaces, underscores, and special chars with dashes
 * - Remove multiple consecutive dashes
 * - Trim dashes from start/end
 */
export function usernameToUrlFormat(username: string): string {
  return username
    .toLowerCase()
    .replace(/[\s_]+/g, '-') // Replace spaces and underscores with dashes
    .replace(/[^a-z0-9-]/g, '-') // Replace other special chars with dashes
    .replace(/-+/g, '-') // Replace multiple dashes with single dash
    .replace(/^-|-$/g, ''); // Remove leading/trailing dashes
}

/**
 * Decode username from URL format back to original format
 * This is a best-effort conversion - we'll match against actual usernames in the ledger
 */
function urlFormatToUsername(urlFormat: string): string {
  // Replace dashes with spaces for matching
  // We'll use this to search the ledger for the actual username
  return urlFormat.replace(/-/g, ' ');
}

const Wapped: React.FC = () => {
  usePageTitle('WINGO World Wapped');
  const { username: usernameParam } = useParams<{ username?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const navigationRef = useRef<WrappedNavigationRef>(null);

  // Support both new path format (/wapped/username) and old query format (?username=...)
  // Prefer path parameter, fallback to query parameter for backwards compatibility
  const usernameFromPath = usernameParam ? urlFormatToUsername(usernameParam) : '';
  const usernameFromQuery = searchParams.get('username') || '';
  const usernameFromUrl = usernameFromPath || usernameFromQuery;
  
  // Redirect old query format to new path format
  useEffect(() => {
    if (usernameFromQuery && !usernameFromPath) {
      const urlFormat = usernameToUrlFormat(usernameFromQuery);
      navigate(`/wapped/${urlFormat}`, { replace: true });
    }
  }, [usernameFromQuery, usernameFromPath, navigate]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [ledgerRows, setLedgerRows] = useState<LedgerRow[]>([]);
  const [logRows, setLogRows] = useState<LogRow[]>([]);
  const [actualUsername, setActualUsername] = useState<string>('');

  // Normalize string for comparison (lowercase, remove accents, normalize spaces/dashes)
  const normalizeForMatching = (str: string): string => {
    return str
      .toLowerCase()
      .normalize('NFD') // Decompose accented characters (é -> e + ´)
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
      .replace(/[\s_-]+/g, ' ') // Normalize spaces, dashes, underscores to spaces
      .trim();
  };

  // Find actual username from ledger (case-insensitive, handles spaces/dashes, accents)
  const findUsernameInLedger = (searchTerm: string, ledger: LedgerRow[]): string | null => {
    if (!searchTerm) return null;
    
    const normalizedSearch = normalizeForMatching(searchTerm);
    
    for (const row of ledger) {
      const normalizedRow = normalizeForMatching(row.Username);
      if (normalizedRow === normalizedSearch) {
        return row.Username; // Return with original capitalization and accents
      }
    }
    
    return null;
  };

  useEffect(() => {
    if (!usernameFromUrl) {
      setError('Please provide a username in the URL: /wapped/username (e.g., /wapped/erock or /wapped/l-o)');
      setLoading(false);
      return;
    }

    // Load and parse CSV data
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load CSV files - try multiple paths
        let ledgerCsv = '';
        let logCsv = '';
        
        const paths = [
          { ledger: '/data/Wrapped.WINGO tracker - Ledger.csv', log: '/data/Wrapped.WINGO tracker - Log.csv' },
          { ledger: '/src/data/Wrapped.WINGO tracker - Ledger.csv', log: '/src/data/Wrapped.WINGO tracker - Log.csv' },
        ];
        
        for (const pathSet of paths) {
          try {
            const [ledgerRes, logRes] = await Promise.all([
              fetch(pathSet.ledger),
              fetch(pathSet.log),
            ]);
            
            if (ledgerRes.ok && logRes.ok) {
              ledgerCsv = await ledgerRes.text();
              logCsv = await logRes.text();
              break;
            }
          } catch (e) {
            // Try next path set
            continue;
          }
        }
        
        if (!ledgerCsv || !logCsv) {
          throw new Error('Failed to load CSV data. Please ensure the CSV files are in /public/data/ or /src/data/');
        }

        // Parse CSVs
        Papa.parse<LedgerRow>(ledgerCsv, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const parsedLedger = results.data.map((row) => ({
              ...row,
              'Total Balance': parseFloat(String(row['Total Balance'])) || 0,
              'Total Mined': parseFloat(String(row['Total Mined'])) || 0,
              'Distance (KM)': parseFloat(String(row['Distance (KM)'])) || 0,
              Rank: parseFloat(String(row.Rank)) || 0,
              Events: parseFloat(String(row.Events)) || 0,
              Wager: parseFloat(String(row.Wager)) || 0,
              'Dancing Daisy': parseFloat(String(row['Dancing Daisy'])) || 0,
              Yellowstone: parseFloat(String(row.Yellowstone)) || 0,
              'Gate Relock': parseFloat(String(row['Gate Relock'])) || 0,
              'OBWI Prize': parseFloat(String(row['OBWI Prize'])) || 0,
              'Wingle (L32)': parseFloat(String(row['Wingle (L32)'])) || 0,
            }));

            setLedgerRows(parsedLedger);

            Papa.parse<LogRow>(logCsv, {
              header: true,
              skipEmptyLines: true,
              complete: (logResults) => {
                const parsedLog = logResults.data.map((row) => ({
                  ...row,
                  ID: parseFloat(String(row.ID)) || 0,
                  'WINGO +/-': parseFloat(String(row['WINGO +/-'])) || 0,
                  'Total KM for Activity': row['Total KM for Activity']
                    ? parseFloat(String(row['Total KM for Activity']))
                    : null,
                  '5-digit': row['5-digit'] ? parseFloat(String(row['5-digit'])) : null,
                }));

                setLogRows(parsedLog);

                  // Find actual username from ledger (handles case-insensitive matching, spaces/dashes)
                  const foundUsername = findUsernameInLedger(usernameFromUrl, parsedLedger);
                  
                  if (!foundUsername) {
                    setError(`User "${usernameFromUrl}" not found in ledger. Please check the username and try again.`);
                    setLoading(false);
                    return;
                  }
                  
                  setActualUsername(foundUsername);

                  // Build stats - function will use correct capitalization from ledger row
                  try {
                    const userStats = buildWrappedStatsForUser(
                      foundUsername,
                      parsedLedger,
                      parsedLog
                    );
                    setStats(userStats);
                } catch (err) {
                  setError(`Failed to build stats: ${err instanceof Error ? err.message : 'Unknown error'}`);
                }

                setLoading(false);
              },
              error: (err) => {
                setError(`Failed to parse Log CSV: ${err.message}`);
                setLoading(false);
              },
            });
          },
          error: (err) => {
            setError(`Failed to parse Ledger CSV: ${err.message}`);
            setLoading(false);
          },
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
        setLoading(false);
      }
    };

    loadData();
  }, [usernameFromUrl]);

  const avatarUrl = getAvatarForUser(actualUsername || usernameFromUrl);

  // Build screens array with useMemo for performance
  // Must be called before early returns to follow Rules of Hooks
  const { screens, slideDurations } = useMemo(() => {
    if (!stats) {
      return { screens: [], slideDurations: [] };
    }
    
    // Check if user has OBWI prize - add special card early in sequence
    const hasOBWIPrize = stats.hasOBWIPrize;
    
    const screensArray: React.ReactElement[] = [
    // 1. TitleCard
    <TitleCard key="title" username={stats.username} avatarUrl={avatarUrl} />,
    // 2. InitiationCard
    <InitiationCard key="initiation" initiationDate={stats.initiationDate} avatarUrl={avatarUrl} username={stats.username} />,
    // 3. WingateSatelliteCard
    <WingateSatelliteCard key="wingateSatellite" avatarUrl={avatarUrl} username={stats.username} />,
    // 4. TotalMinedCard
    <TotalMinedCard
      key="totalMined"
      totalWingoMined={stats.totalWingoMined}
      distanceKM={stats.distanceKM}
      avatarUrl={avatarUrl}
      username={stats.username}
    />,
    // 5. HighestSessionCard
    <HighestSessionCard key="highestSession" highestSession={stats.highestWingoSession} avatarUrl={avatarUrl} username={stats.username} />,
    // 6. WingoPerSessionCard
    <WingoPerSessionCard
      key="wingoPerSession"
      avgWingoPerSession={stats.avgWingoPerSession}
      avatarUrl={avatarUrl}
      username={stats.username}
    />,
    // 7. MinedWithDaisyCard
    <MinedWithDaisyCard key="minedWithDaisy" username={stats.username} totalWingoMined={stats.totalWingoMined} />,
    // 8. DaisyCategoryCard (DAISY's Designation) - Consistent position for all users
    <DaisyCategoryCard key="daisyCategory" username={stats.username} />,
    // 9. WagersCard
    <WagersCard
      key="wagers"
      totalWagers={stats.totalWagers}
      netWagerWingo={stats.netWagerWingo}
      avatarUrl={avatarUrl}
      username={stats.username}
    />,
    // 10. WingonomicsCard (Ad)
    <WingonomicsCard
      key="wingonomics"
      username={stats.username}
    />,
    // 11. WingipediaCard (Ad)
    <WingipediaCard
      key="wingipedia"
      username={stats.username}
    />,
    // 12. OldBalanceCard – 8 sec forced ad
    <OldBalanceCard
      key="oldBalance"
      onTimerComplete={() => {}}
      navigationRef={navigationRef}
      username={stats.username}
    />,
    // 13. SpecialUnlocksCard
    <SpecialUnlocksCard
      key="specialUnlocks"
      hasOBWIPrize={stats.hasOBWIPrize}
      hasYellowstoneCompletion={stats.hasYellowstoneCompletion}
      hasDancingDaisy={stats.hasDancingDaisy}
      hasGateUnlock={stats.hasGateUnlock}
      avatarUrl={avatarUrl}
      username={stats.username}
    />,
    ];
    
    // 14. OBWIVideoCard (conditional)
    if (stats.hasGateUnlock) {
      screensArray.push(
        <OBWIVideoCard
          key="obwiVideo"
          username={stats.username}
        />
      );
    }
    
    // 15. OBWIPrizeCard (conditional)
    if (hasOBWIPrize) {
      screensArray.push(
        <OBWIPrizeCard key="obwiPrize" username={stats.username} avatarUrl={avatarUrl} obwiPrizeAmount={stats.obwiPrizeAmount} rank={stats.obwiRank} />
      );
    }
    
    screensArray.push(
    // 16. NextYearCard
    <NextYearCard
      key="nextYear"
      totalWingoMined={stats.totalWingoMined}
      daisyTrustScore={stats.daisyTrustScore}
      avatarUrl={avatarUrl}
      username={stats.username}
      rank={stats.rank}
    />,
    // 17. SaveTheDateCard
    <SaveTheDateCard
      key="saveTheDate"
      username={stats.username}
    />,
    // 18. ShareCard
    <ShareCard key="share" stats={stats} avatarUrl={avatarUrl} username={stats.username} />,
    );
    
    // Create slide durations array - OBWIVideoCard gets 15 seconds (15000ms), OldBalanceCard gets 8 seconds (8000ms), others get default 4 seconds
    const slideDurationsArray: number[] = screensArray.map((screen, index) => {
      // Check if this is the OBWIVideoCard by checking the key
      if (React.isValidElement(screen) && screen.key === 'obwiVideo') {
        return 15000; // 15 seconds for video
      }
      // Check if this is the OldBalanceCard by checking the key
      if (React.isValidElement(screen) && screen.key === 'oldBalance') {
        return 8000; // 8 seconds for forced ad countdown
      }
      return 4000; // Default 4 seconds
    });
    
    return { screens: screensArray, slideDurations: slideDurationsArray };
  }, [stats, avatarUrl, navigationRef]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="text-2xl font-bold text-wingo-600 mb-4">Loading your Wapped...</div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-wingo-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="text-2xl font-bold text-red-600 mb-4">Error</div>
          <p className="text-gray-700 mb-4">{error || 'Failed to load wapped data'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-wingo-600 text-white rounded-lg hover:bg-wingo-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <WrappedProvider username={stats.username} avatarUrl={avatarUrl}>
      <div 
        className="fixed inset-0 w-full overflow-hidden"
        style={{ 
          height: '100dvh', // Dynamic viewport height accounts for browser chrome
          maxHeight: '100dvh'
        }}
      >
        <WrappedScreenWrapper ref={navigationRef} screens={screens} slideDurations={slideDurations} />
      </div>
    </WrappedProvider>
  );
};

export default Wapped;

